// src/components/resources/calculators/MarketingBudgetCalculator.logic.ts

/**
 * Pure (non-`'use client'`) budget math for the Marketing Budget Calculator,
 * factored out so the numeric core can be unit-tested without a DOM and the
 * component stays focused on presentation. See
 * `__tests__/MarketingBudgetCalculator.logic.test.ts`.
 */

export type Tier = 'core' | 'growth' | 'experimental'

export interface Channel {
  id: string
  name: string
  tier: Tier
  pct: number
}

/** 70/20/10 Core/Growth/Experimental framework targets. */
export const TIER_TARGETS: Record<Tier, number> = { core: 70, growth: 20, experimental: 10 }

/**
 * Healthy allocation bands around the 70/20/10 targets. A tier is "on track"
 * when its share falls inside its band — more forgiving (and more honest) than
 * a hard 70/20/10, since real mixes vary.
 */
export const TIER_RANGES: Record<Tier, readonly [number, number]> = {
  core: [60, 80],
  growth: [10, 30],
  experimental: [5, 15],
}

export const TIER_ORDER: Tier[] = ['core', 'growth', 'experimental']

/** Whether a tier's allocation falls within its healthy band. */
export function tierInRange(actual: number, tier: Tier): boolean {
  const [lo, hi] = TIER_RANGES[tier]
  return actual >= lo && actual <= hi
}

/**
 * Overall budget-health verdict: every tier within its band AND the mix sums
 * to 100%. `score` (0–100) is the share of the four checks that pass, for the
 * gauge fill.
 */
export function budgetHealth(
  tierTotals: Record<Tier, number>,
  totalPct: number,
): { onTrack: boolean; label: string; score: number } {
  const checks = [
    tierInRange(tierTotals.core, 'core'),
    tierInRange(tierTotals.growth, 'growth'),
    tierInRange(tierTotals.experimental, 'experimental'),
    totalPct === 100,
  ]
  const passed = checks.filter(Boolean).length
  const onTrack = passed === checks.length
  return {
    onTrack,
    label: onTrack ? 'On track' : 'Tune your mix',
    score: Math.round((passed / checks.length) * 100),
  }
}

/**
 * Coerce a number to finite and non-negative (NaN/Infinity → 0). A
 * `<input type=number>` in a transient-invalid state (e.g. "1e") yields `NaN`
 * from `+value`; without this guard that `NaN` flows into `formatCurrency`,
 * which throws on non-finite input and blanks the calculator.
 */
export function clampNonNegative(n: number): number {
  return Number.isFinite(n) ? Math.max(0, n) : 0
}

/** Coerce a number to a finite percentage in [0, 100] (NaN/Infinity → 0). */
export function clampPercent(n: number): number {
  return Number.isFinite(n) ? Math.min(100, Math.max(0, n)) : 0
}

/**
 * Resolve the annual budget from the chosen input method. Always returns a
 * finite, non-negative number — inputs are clamped, so a transient `NaN` from a
 * number field can never propagate into currency formatting.
 */
export function computeAnnualBudget(opts: {
  method: 'total' | 'revenue'
  period: 'annual' | 'monthly'
  budgetInput: number
  revenue: number
  revenuePct: number
}): number {
  const budgetInput = clampNonNegative(opts.budgetInput)
  const revenue = clampNonNegative(opts.revenue)
  const revenuePct = clampPercent(opts.revenuePct)
  if (opts.method === 'revenue') return Math.round((revenue * revenuePct) / 100)
  return opts.period === 'annual' ? budgetInput : budgetInput * 12
}

/** Sum of channel percentages (ignores any non-finite pct defensively). */
export function sumChannelPct(channels: ReadonlyArray<Channel>): number {
  return channels.reduce((sum, c) => sum + (Number.isFinite(c.pct) ? c.pct : 0), 0)
}

/** Percentage allocated per tier. */
export function computeTierTotals(channels: ReadonlyArray<Channel>): Record<Tier, number> {
  const totals: Record<Tier, number> = { core: 0, growth: 0, experimental: 0 }
  for (const c of channels) totals[c.tier] += Number.isFinite(c.pct) ? c.pct : 0
  return totals
}

/**
 * Scale channel percentages to sum to 100 (whole points), assigning any
 * rounding drift to the largest channel. Returns a copy; a non-positive total
 * (e.g. empty list) is returned unchanged.
 */
export function normalizeChannels(channels: ReadonlyArray<Channel>): Channel[] {
  const sum = sumChannelPct(channels)
  if (sum <= 0) return channels.slice()
  const scaled = channels.map((c) => ({ ...c, pct: Math.round((c.pct / sum) * 100) }))
  const drift = 100 - scaled.reduce((s, c) => s + c.pct, 0)
  if (drift !== 0 && scaled.length > 0) {
    const largest = scaled.reduce((a, b) => (b.pct > a.pct ? b : a))
    largest.pct += drift
  }
  return scaled
}

export interface TierStatus {
  label: string
  /** Whether the tier is within ±5pt of its 70/20/10 target. */
  onTarget: boolean
}

/** Human-readable status of a tier's allocation vs. its framework target. */
export function tierStatus(actual: number, target: number): TierStatus {
  const delta = actual - target
  if (Math.abs(delta) <= 5) return { label: 'on target', onTarget: true }
  return {
    label: delta > 0 ? `${Math.round(delta)}pt over` : `${Math.round(-delta)}pt under`,
    onTarget: false,
  }
}
